import { Handle, Position } from "@xyflow/react";
import classNames from "classnames";
import { get, isEmpty, isNil } from "lodash";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { NODE_TYPES } from "utils/constants";
import { reportIssueCount } from "./BindingReport";
import { nodeTypeMeta } from "./node-types";
import ScreenThumbnail from "./ScreenThumbnail";

const nodeTypeClass = (nodeType) =>
  `canvas-node--${String(nodeType || NODE_TYPES.STATUS)
    .toLowerCase()
    .replace(/_/g, "_")}`;

const NodeShell = ({ nodeType, Icon, name, badge, children }) => {
  const meta = nodeTypeMeta(nodeType);
  const accent = meta.color || COLORS.C_PRIMARY;

  return (
    <div
      className={classNames("canvas-node", nodeTypeClass(nodeType))}
      data-accent={accent}
    >
      <Handle type="target" position={Position.Left} />
      <div className="canvas-node__accent" />
      <div className="canvas-node__body">
        <div className="canvas-node__header">
          <span className="canvas-node__title-wrap">
            {Icon ? <Icon size={18} color={accent} /> : null}
            <strong className="canvas-node__title">{name || "—"}</strong>
          </span>
          {badge ? <span className="canvas-node__badge">{badge}</span> : null}
        </div>
        {children}
      </div>
      <Handle type="source" position={Position.Right} />
    </div>
  );
};

NodeShell.propTypes = {
  nodeType: PropTypes.string.isRequired,
  Icon: PropTypes.elementType,
  name: PropTypes.string,
  badge: PropTypes.string,
  children: PropTypes.node,
};

NodeShell.defaultProps = {
  Icon: null,
  name: null,
  badge: null,
  children: null,
};

const StatusNode = ({ data }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const STATUS_NODE = t("STATUS_NODE", { returnObjects: true });
  const nodeType = data.nodeType || NODE_TYPES.STATUS;
  const meta = nodeTypeMeta(nodeType);
  const Icon = meta.icon;

  if (nodeType !== NODE_TYPES.STATUS) {
    const fields = get(data, "formDefinition.fields") || [];
    const configKeys = Object.keys(data.config || {});
    return (
      <NodeShell nodeType={nodeType} Icon={Icon} name={data.name} badge={nodeType}>
        {nodeType === NODE_TYPES.FORM_STEP ? (
          <div className="canvas-node__meta">
            {t("STATUS_NODE.FIELD_COUNT", { count: fields.length })}
          </div>
        ) : !isEmpty(configKeys) ? (
          <div className="canvas-node__meta-sm">
            {configKeys.slice(0, 4).join(", ")}
          </div>
        ) : (
          <div className="canvas-node__meta-muted">{data.progress || "—"}</div>
        )}
      </NodeShell>
    );
  }

  const hasPreview = !isNil(data.screenHtml) && data.screenHtml !== "";
  const report = data.screenReport;
  const issueCount = reportIssueCount(report);

  return (
    <NodeShell nodeType={nodeType} Icon={Icon} name={data.name} badge={data.tag}>
      {data.progress ? (
        <div className="canvas-node__progress">{data.progress}</div>
      ) : null}
      {hasPreview ? (
        <div className="canvas-node__preview">
          <ScreenThumbnail html={data.screenHtml} size="sm" title={data.name} />
        </div>
      ) : (
        <div
          className={classNames("canvas-node__meta-sm", {
            "canvas-node__meta-muted": !data.screenCount,
          })}
        >
          {data.screenCount
            ? t("STATUS_NODE.UNPUBLISHED_SCREENS", { count: data.screenCount })
            : STATUS_NODE.NO_SCREEN}
        </div>
      )}
      {!isNil(report) ? (
        <div className="canvas-editor__subsection-sm">
          {issueCount > 0 ? (
            <span className="canvas-node__binding-warning">
              {`⚠ ${t("STATUS_NODE.ISSUES", { count: issueCount })}`}
            </span>
          ) : (
            <span className="canvas-node__binding-ok">
              {`✓ ${STATUS_NODE.BINDING_OK}`}
            </span>
          )}
        </div>
      ) : null}
    </NodeShell>
  );
};

StatusNode.propTypes = {
  data: PropTypes.object.isRequired,
};

export default StatusNode;
